import { Components, CSSInterpolation } from '@mui/material';
import { Property } from 'csstype';
import { Theme } from '@mui/material/styles';
import { answerToggleButtonClasses } from 'component/AnswerToggleButton/classes';
import { colors, focusBorderColor, inputBorderWidth } from 'app/theme/config';

export const answerToggleButtonBorderWidth = {
  light: inputBorderWidth,
  highContrastAccessible: inputBorderWidth,
};

// Width (px) of the option-coloured frame drawn around a selected answer button.
export const selectedFrameWidth = 2;

export type SelectedFrameSide = 'top' | 'right' | 'bottom' | 'left';

// Builds the inset box-shadow for the selected frame. `omit` drops one side — the group uses this
// for the first/last cells so their outer edge keeps its original border. Drawn as a box-shadow
// (not a real border) so selecting never changes the cell size in the collapsed-border layout.
export const selectedFrameShadow = (color: Property.Color, omit?: SelectedFrameSide): string => {
  const w = selectedFrameWidth;
  const sides = {
    top: `inset 0 ${w}px 0 0 ${color}`,
    right: `inset -${w}px 0 0 0 ${color}`,
    bottom: `inset 0 -${w}px 0 0 ${color}`,
    left: `inset ${w}px 0 0 0 ${color}`,
  };
  return (['top', 'right', 'bottom', 'left'] as const)
    .filter((side) => side !== omit)
    .map((side) => sides[side])
    .join(', ');
};

// Selected-option frame, shared by both themes (light: per-option colour, HCB: a single malibu).
// Recolours the box border and draws a uniform frame on all four sides, and exposes the colour as
// the CSS var `--wb-answer-selected-border` so the group can rebuild the frame for the first/last
// cells (dropping the outer edge — see AnswerToggleButtonGroup.s.ts).
const selectedBoxFrame = (color: Property.BorderColor): CSSInterpolation => ({
  borderColor: color,
  '--wb-answer-selected-border': color,
  boxShadow: selectedFrameShadow(color),
});

const answerVariantStyle = ({
  hoverBgColor,
  borderLineColor,
}: {
  hoverBgColor: Property.BackgroundColor;
  borderLineColor: Property.BorderColor;
}): CSSInterpolation => ({
  backgroundColor: 'white',
  position: 'relative',
  [`& .${answerToggleButtonClasses.borderLine}`]: {
    backgroundColor: borderLineColor,
  },
  '&:hover': {
    backgroundColor: hoverBgColor,
  },
  [`&.${answerToggleButtonClasses.selected}`]: {
    backgroundColor: hoverBgColor,
    '&:hover': {
      backgroundColor: hoverBgColor,
    },
    [`& .${answerToggleButtonClasses.containerBox}`]: selectedBoxFrame(borderLineColor),
  },
});

const light: Components<Theme>['WBAnswerToggleButton'] = {
  styleOverrides: {
    option1: answerVariantStyle({
      hoverBgColor: '#EEF6E6',
      borderLineColor: '#64B209',
    }),
    option2: answerVariantStyle({
      hoverBgColor: '#F3F7E6',
      borderLineColor: '#8BB209',
    }),
    option3: answerVariantStyle({
      hoverBgColor: '#FFF8E5',
      borderLineColor: '#FFB100',
    }),
    option4: answerVariantStyle({
      hoverBgColor: '#FDEFE5',
      borderLineColor: '#E76202',
    }),
    option5: answerVariantStyle({
      hoverBgColor: '#FDE6E6',
      borderLineColor: '#DD0606',
    }),
    notApplicable: answerVariantStyle({
      hoverBgColor: '#ECECEC',
      borderLineColor: '#414141',
    }),
    containerBox: {
      borderColor: colors.alto,
      borderWidth: answerToggleButtonBorderWidth.light,
    },
    focusVisibleLine: {
      borderColor: focusBorderColor.light,
    },
  },
};

const highContrastAccessible: Components<Theme>['WBAnswerToggleButton'] = {
  styleOverrides: {
    root: {
      color: colors.candyCorn,
      boxSizing: 'border-box',

      [`&.${answerToggleButtonClasses.selected}`]: {
        color: colors.malibu,

        '.WBAnswerToggleButton-borderLine': {
          background: colors.malibu,
        },

        // Same selected frame as the light theme, but a single malibu colour for every option.
        // Contract: HCB applies the frame on `root`, so every selected option (incl. the `default`
        // variant) is framed; the light theme frames per option-variant, so only the coloured
        // opinion variants get a frame there.
        [`& .${answerToggleButtonClasses.containerBox}`]: selectedBoxFrame(colors.malibu),
      },

      // Hover whitens the accent line only on unselected options; a selected option must keep its
      // malibu accent on hover (otherwise it loses its selected colour).
      [`&:not(.${answerToggleButtonClasses.selected}):hover`]: {
        '.WBAnswerToggleButton-borderLine': {
          background: colors.white,
        },
      },

      [`&.${answerToggleButtonClasses.selected} > .${answerToggleButtonClasses.focusVisibleLine}`]:
        {
          border: 'none',
        },
    },

    containerBox: {
      borderColor: colors.white,
      borderWidth: answerToggleButtonBorderWidth.highContrastAccessible,
    },

    focusVisibleLine: {
      borderColor: focusBorderColor.highContrastAccessible,
    },
  },
};

export const WBAnswerToggleButtonComp = {
  light,
  highContrastAccessible,
};
